import React, { ChangeEvent, ReactElement, useEffect } from 'react';

import {
    Box,
    DialogContent,
    Typography,
    TextField,
    Grid2,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Chip,
} from '@mui/material';
import { DimensionType, IArticleDto, LabelCategory } from '../../api/ClientApi';
import {
    clearArticle,
    createNewEmptyArticle,
    importArticle,
    removeLabelFromArticle,
    saveArticle,
    updateArticleDescription,
    updateArticleDimensionType,
    updateArticleInnerCode,
    updateArticleManufacturer,
    updateArticleOutterCode,
    updateArticleTitle,
} from '../../redux/actions/articleActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { getArticle } from '../../redux/selectors/articlesSelector';
import { RootState } from '../../redux/reducers';
import { SaveEntityStatus } from '../../redux/reducers/common';
import { DimensionTypeDimensionSR, DimensionTypeSR } from '../../common/Types';
import ArticleLabelAutocomplete from '../../common/components/autocomplete/ArticleLabelAutocomplete';
import { clearLabels, removeLabelEntity } from '../../redux/actions/labelActions';
import { WarehouseClient } from '../../api/Extensions';

import { FormActions, FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

export interface ArticleFormProps {
    open: boolean;
    closeForm(): void;
    articleId?: number;
}

export default function ArticleForm(props: ArticleFormProps): ReactElement {
    const { open, closeForm, articleId } = props;

    const article: IArticleDto | null = useAppSelector(getArticle);
    const saveStatus = useAppSelector((state: RootState) => state.article.saveStatus);
    const responseMessage = useAppSelector((state: RootState) => state.article.responseMessage);

    const labelEntities = useAppSelector((state) => state.label.labelEntities);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!!articleId && articleId > 0) {
            dispatch(importArticle(articleId));
        } else {
            if (!article) {
                dispatch(createNewEmptyArticle());
            }
        }
    }, [dispatch, articleId]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleInputElementChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'innerCode':
                dispatch(updateArticleInnerCode(value));
                break;
            case 'outterCode':
                dispatch(updateArticleOutterCode(value));
                break;
            case 'title':
                dispatch(updateArticleTitle(value));
                break;
            case 'manufacturer':
                dispatch(updateArticleManufacturer(value));
                break;
            case 'description':
                dispatch(updateArticleDescription(value));
                break;
            default:
                console.log('invalid field name');
        }
    };

    const handleSelectDimensionType = (event: SelectChangeEvent<DimensionType>) => {
        const value = event.target.value;

        switch (value) {
            case DimensionType[DimensionType.Length]:
                dispatch(updateArticleDimensionType(DimensionType.Length));
                break;
            case DimensionType[DimensionType.Quantity]:
                dispatch(updateArticleDimensionType(DimensionType.Quantity));
                break;
            case DimensionType[DimensionType.Weight]:
                dispatch(updateArticleDimensionType(DimensionType.Weight));
                break;
            default:
                dispatch(updateArticleDimensionType(undefined));
        }
    };

    const handleRemoveLabelEntity = (labelIndex: number) => {
        const labelEntity = labelEntities?.find((l) => l.index === labelIndex);

        if (!!labelEntity?.label?.id && labelEntity.label.id > 0 && !!article?.id && article.id > 0) {
            WarehouseClient.removeLabelFromArticle(labelEntity?.label.id ?? 0, article.id).then(() => {
                dispatch(removeLabelEntity(labelIndex));
                dispatch(removeLabelFromArticle(labelEntity.label?.id ?? 0));
            });
        } else {
            dispatch(removeLabelEntity(labelIndex));
        }
    };

    const handleSave = () => {
        dispatch(saveArticle());
    };

    const handleClose = () => {
        dispatch(clearArticle());
        dispatch(clearLabels());
        closeForm();
    };

    const isArticleDataValid =
        !!article &&
        !!article.innerCode &&
        article.innerCode !== '' &&
        !!article.outterCode &&
        article.outterCode !== '' &&
        !!article.title &&
        article.title !== '' &&
        !!article.dimensionType;

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose} disableEscapeKeyDown>
            <FormDialogTitle>Artikal - {!article?.id ? 'dodavanje' : 'izmena'}</FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: 1000, margin: 'auto' }}>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            error={!!article?.title && article?.title !== '' ? false : true}
                            required
                            fullWidth
                            name="title"
                            label="Naziv"
                            value={!!article?.title ? article?.title : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {labelEntities?.map((le) => {
                                return (
                                    <Chip
                                        key={le.index}
                                        color="primary"
                                        variant="outlined"
                                        label={le.label?.title ?? ''}
                                        onDelete={() => handleRemoveLabelEntity(le.index)}
                                    />
                                );
                            })}
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <ArticleLabelAutocomplete isRequired={false} />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 6 }} />
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!!article?.manufacturer && article?.manufacturer !== '' ? false : true}
                            required
                            fullWidth
                            name="manufacturer"
                            label="Proizvođač"
                            value={!!article?.manufacturer ? article?.manufacturer : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!!article?.outterCode && article?.outterCode !== '' ? false : true}
                            required
                            fullWidth
                            name="outterCode"
                            label="Šifra artikla proizvođača"
                            value={!!article?.outterCode ? article?.outterCode : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!!article?.innerCode && article?.innerCode !== '' ? false : true}
                            required
                            fullWidth
                            name="innerCode"
                            label="Interni kod proizvoda"
                            value={!!article?.innerCode ? article?.innerCode : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!!article?.dimensionType ? false : true} required>
                            <InputLabel id="demo-simple-select-label">Merna dimenzija</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={article?.dimensionType ?? ''}
                                label="Merna dimenzija"
                                onChange={handleSelectDimensionType}
                            >
                                {Object.values(DimensionType).map((dt) => {
                                    return (
                                        <MenuItem key={dt} value={dt as DimensionType}>
                                            {DimensionTypeSR[dt] + ' (' + DimensionTypeDimensionSR[dt] + ')'}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            required={false}
                            fullWidth
                            name="description"
                            label="Opis"
                            multiline
                            rows={6}
                            value={!!article?.description ? article?.description : ''}
                            variant="outlined"
                            onChange={handleInputElementChange}
                        />
                    </Grid2>
                    {saveStatus === SaveEntityStatus.Error && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*{responseMessage ?? 'došlo je do nepoznate greške'}</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isArticleDataValid || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
